import React, { useState, useEffect } from "react";
import { Map } from "pigeon-maps";

// Define available map styles with their respective Mapbox IDs
const MAPBOX_STYLES = {
  light: { id: "lececil123/cm1quujef00gt01pl4616glxy", label: "Standard" },
  dark: { id: "lececil123/cm1qui5d500ss01ph2qh3efh9", label: "Satellite" },
  terrain: { id: "lececil123/cm6kw85rl009w01sdb0hl1c5v", label: "Navigation" },
  night: { id: "lececil123/cm6kvyvhi00ia01qr7ajegmwo", label: "Night Mode" },
};

// Helper function to generate Mapbox tile URLs
const getMapProvider = (styleId: string) => (x: number, y: number, z: number, dpr: number) =>
  `https://api.mapbox.com/styles/v1/${styleId}/tiles/512/${z}/${x}/${y}${dpr >= 2 ? "@2x" : ""}?access_token=${process.env.GATSBY_MAPBOX_ACCESS_TOKEN}`;

const MapStyleSwitcher = ({ onStyleChange }: { onStyleChange: (style: string) => void }) => {
  const [mapStyle, setMapStyle] = useState<string>(MAPBOX_STYLES.light.id);

  useEffect(() => {
    // Load user preference or detect system dark mode
    const savedStyle = localStorage.getItem("mapStyle");
    if (savedStyle) {
      setMapStyle(savedStyle);
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      setMapStyle(prefersDark ? MAPBOX_STYLES.dark.id : MAPBOX_STYLES.light.id);
    }
  }, []);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    // Sync with system dark mode
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem("mapStyle")) {
        const newStyle = e.matches ? MAPBOX_STYLES.dark.id : MAPBOX_STYLES.light.id;
        setMapStyle(newStyle);
        onStyleChange(newStyle);
      }
    };
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [onStyleChange]);

  const handleStyleChange = (styleId: string) => {
    setMapStyle(styleId);
    localStorage.setItem("mapStyle", styleId);
    onStyleChange(styleId);
    setIsDropdownOpen(false);
  };

  return (
    <div className="map-style-switcher">
      <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="map-style-toggle">
        Change Map
      </button>

      {isDropdownOpen && (
        <div className="map-style-dropdown">
          {Object.entries(MAPBOX_STYLES).map(([key, { id, label }]) => (
            <div key={id} className={`map-style-option ${mapStyle === id ? "selected" : ""}`} onClick={() => handleStyleChange(id)}>
              <div className="map-style-preview">
                <Map provider={getMapProvider(id)} center={[50, 14]} zoom={4} dprs={[1, 2]} />
              </div>
              <span className="mapLabel">{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MapStyleSwitcher;
